import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * ArticleContent — renders Help Center / Knowledge Base article bodies safely.
 *
 * Handles the mixed content in the DB where legacy seeded articles have a
 * leading HTML `<img/>` hero followed by a Markdown body (e.g. "# Title\n## Section").
 * `marked` treats inline HTML as passthrough so a mixed doc renders correctly.
 * DOMPurify strips anything unsafe before injecting.
 *
 * Fixes site audit follow-up: raw Markdown headers were rendering as text.
 */
const markedOptions = {
  breaks: true,
  gfm: true,
};

const purifyOptions = {
  ADD_ATTR: ['target'],
};

const looksLikeHtml = (str) => {
  // If the string contains block-level HTML tags AND no markdown headers/lists,
  // skip the markdown pass to avoid double-conversion cost.
  if (!str) return false;
  const hasBlockHtml = /<(p|div|h[1-6]|ul|ol|li|table|section|article|blockquote)[\s>]/i.test(str);
  const hasMarkdown = /(^|\n)\s{0,3}(#{1,6}\s|\*\s|-\s|\d+\.\s|>\s)/.test(str);
  return hasBlockHtml && !hasMarkdown;
};

// Some seeded articles concatenate an HTML hero (`<p><img/></p>`) directly to
// a Markdown body without a blank line, which prevents marked from parsing
// the first `# header`. Insert a blank line between a closing HTML block tag
// and a following Markdown header/list bullet so marked can pick it up.
const normalizeMixed = (str) => {
  if (!str) return str;
  return str
    .replace(/(<\/(?:p|div|h[1-6]|ul|ol|li|table|section|article|blockquote)>)(?=\s*[#*\->\d])/gi, '$1\n\n')
    // Ensure a blank line before markdown headers that follow inline content.
    .replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');
};

const ArticleContent = ({ content, className = '', testId = 'article-content' }) => {
  const html = useMemo(() => {
    const raw = content || '';
    let rendered;
    if (looksLikeHtml(raw)) {
      rendered = raw;
    } else {
      try {
        rendered = marked.parse(normalizeMixed(raw), markedOptions);
      } catch (err) {
        // If parsing blows up, fall back to raw so text at least shows.
        rendered = raw;
      }
    }
    return DOMPurify.sanitize(rendered, purifyOptions);
  }, [content]);

  return (
    <div
      className={`prose prose-sky max-w-none prose-img:rounded-lg prose-img:my-3 ${className}`}
      data-testid={testId}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default ArticleContent;
